import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Bell,
  X,
  Clock,
  Check,
  MoreVertical,
  Phone,
  Calendar,
  Users,
  AlertCircle,
} from 'lucide-react';
import api from '../services/api';

const REMINDER_TYPE_ICONS = {
  follow_up: Calendar,
  call: Phone,
  meeting: Users,
};

const SNOOZE_OPTIONS = [
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '1 day', value: 1440 },
];

export const ReminderPopup = ({ reminder, onDismiss, onUpdate }) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const TypeIcon = REMINDER_TYPE_ICONS[reminder.type] || Calendar;
  
  const handleComplete = async () => {
    setIsLoading(true);
    try {
      await api.post(`/reminders/${reminder.id}/complete`);
      toast.success('Reminder marked as complete');
      onUpdate?.();
      onDismiss();
    } catch (error) {
      toast.error('Failed to complete reminder');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSnooze = async (minutes) => {
    setIsLoading(true);
    try {
      await api.post(`/reminders/${reminder.id}/snooze?minutes=${minutes}`);
      toast.success(`Reminder snoozed for ${minutes >= 60 ? `${minutes / 60} hour(s)` : `${minutes} minutes`}`);
      onUpdate?.();
      onDismiss();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };
  
  const isOverdue = new Date(reminder.scheduledAt) < new Date();
  
  return (
    <Card 
      className={`w-80 shadow-lg border-l-4 ${isOverdue ? 'border-l-red-500' : 'border-l-primary'} animate-in slide-in-from-right-5`}
      data-testid="reminder-popup"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-full ${isOverdue ? 'bg-red-100' : 'bg-primary/10'}`}>
              <TypeIcon className={`h-4 w-4 ${isOverdue ? 'text-red-500' : 'text-primary'}`} />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold">{reminder.title}</CardTitle>
              {isOverdue && (
                <Badge variant="destructive" className="text-xs mt-1">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onDismiss}
            data-testid="reminder-dismiss-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">{reminder.leadName}</p>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatTime(reminder.scheduledAt)}
          </div>
          {reminder.description && (
            <p className="text-xs mt-1 line-clamp-2">{reminder.description}</p>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleComplete}
            disabled={isLoading}
            className="flex-1"
            data-testid="reminder-complete-btn"
          >
            <Check className="h-4 w-4 mr-1" />
            Done
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isLoading}
                data-testid="reminder-snooze-btn"
              >
                <Clock className="h-4 w-4 mr-1" />
                Snooze
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SNOOZE_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleSnooze(option.value)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => window.open(`/leads-board?leadId=${reminder.leadId}`, '_self')}>
                View Lead
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReminderPopup;
